
import React, { useState, useRef } from 'react';
import { Attachment, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface ChatInputUIProps {
  onSendMessage: (content: string, attachments?: Attachment[]) => void;
  isLoading: boolean;
  language: Language;
}

const ChatInputUI: React.FC<ChatInputUIProps> = ({ onSendMessage, isLoading, language }) => {
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';

  const handleSend = () => {
    if ((inputValue.trim() || attachments.length > 0) && !isLoading) {
      onSendMessage(inputValue.trim(), attachments);
      setInputValue('');
      setAttachments([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    (Array.from(files) as File[]).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAttachments(prev => [...prev, {
          name: file.name,
          type: file.type,
          data: ev.target?.result as string,
          url: URL.createObjectURL(file)
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  return (
    <div className="p-4 lg:p-8 bg-white/90 dark:bg-[#0f0f13]/90 backdrop-blur-2xl border-t border-slate-100 dark:border-white/5">
      <div className="max-w-4xl mx-auto">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-4 animate-in slide-in-from-bottom-2">
            {attachments.map((file, i) => (
              <div key={i} className="relative w-16 h-16 rounded-2xl overflow-hidden border-2 border-emerald-500/30 group">
                {file.type.startsWith('image/') ? <img src={file.url} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-400"><i className="fas fa-file text-xl"></i></div>}
                <button onClick={() => setAttachments(p => p.filter((_, idx) => idx !== i))} className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"><i className="fas fa-times"></i></button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-3 bg-slate-100/80 dark:bg-white/[0.04] p-3 rounded-[30px] border border-slate-200/50 dark:border-white/[0.06] transition-all focus-within:ring-2 focus-within:ring-emerald-500/20">
          <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" multiple accept="image/*,.pdf,.doc,.docx,.txt,.json" />
          <button onClick={() => fileInputRef.current?.click()} className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-emerald-500 shrink-0 rounded-2xl">
            <i className="fas fa-paperclip text-xl"></i>
          </button>
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t.placeholder}
            className={`flex-1 bg-transparent border-none focus:ring-0 text-xs lg:text-sm font-bold text-slate-700 dark:text-emerald-50 resize-none py-2.5 placeholder:text-[10px] lg:placeholder:text-sm placeholder:text-slate-400 ${isRtl ? 'text-right' : 'text-left'}`}
            rows={1}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
          />
          <button onClick={handleSend} disabled={(!inputValue.trim() && attachments.length === 0) || isLoading} className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all shrink-0 ${ (inputValue.trim() || attachments.length > 0) && !isLoading ? 'bg-emerald-500 text-white shadow-xl active:scale-90' : 'bg-slate-200 dark:bg-white/5 text-slate-400 opacity-50' }`}>
            <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : (isRtl ? 'fa-paper-plane-top' : 'fa-paper-plane')} text-lg`}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInputUI;
